import type { ParagraphContactPerson as ParagraphContactPersonData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphContactPerson.module.css";

/**
 * Layout 30 - `PARAGRAPH_CONTACT_PERSON_LAYOUT`: karta kontaktowa (osoba decyzyjna).
 *
 * Wygląd:
 *   - Zdjęcie tła u góry karty (16:10) + ciemna nakładka gradientowa
 *   - Tytuł nakładany na dolną część zdjęcia (plain text, białym tekstem)
 *   - Poniżej zdjęcia: imię i rola (HTML z TinyMCE)
 *   - Duży zielony przycisk CTA (accent color) - pełna szerokość karty
 *   - Sekcja kontaktu: ikona Nucleo Outline + label (plain) + numer telefonu (link tel:)
 *
 * Dedykowany layout dla `w_descriptive_window` w sidebar'ze artykułu (np. w
 * `LCatalogTwoColsRight` przy widoku realizacji).
 */
export function ParagraphContactPerson({
  paragraph,
}: {
  paragraph: ParagraphContactPersonData;
  t: ParagraphTranslations;
}) {
  const {
    image,
    text,
    text_1,
    text_2,
    button_name,
    button_url,
    contact_icon,
    contact_phone,
  } = paragraph;

  const hasContact = Boolean(contact_icon || text_2 || contact_phone);

  return (
    <section className={styles.card}>
      <div
        className={styles.hero}
        style={image ? { backgroundImage: `url(${image})` } : undefined}
      >
        <div className={styles.heroOverlay} aria-hidden="true" />
        {text ? (
          <div className={styles.heroTitle}>
            <p>{text}</p>
          </div>
        ) : null}
      </div>

      <div className={styles.body}>
        {text_1 ? (
          <div className={styles.person}>
            <HtmlText html={text_1} />
          </div>
        ) : null}

        {button_name && button_url ? (
          <a href={button_url} className={styles.cta}>
            {button_name}
          </a>
        ) : null}

        {hasContact ? (
          <div className={styles.contact}>
            {contact_icon ? (
              <i
                className={`${styles.contactIcon} nc-icon-outline ${contact_icon}`}
                aria-hidden="true"
              />
            ) : null}
            {text_2 ? <p className={styles.contactLabel}>{text_2}</p> : null}
            {contact_phone ? (
              <p className={styles.contactPhone}>
                <a href={`tel:${contact_phone.replace(/\s+/g, "")}`}>
                  {contact_phone}
                </a>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
